import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, Area, ComposedChart, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import {
  getRiFuelSummary, getRiFuelCheapest, getRiFuelTrend, getRiFuelByMarket,
} from '../../../api/auth';
import { RiLoading, RiEmpty, RiError, InsightCard, RiSparseNotice, RiPagination } from '../primitives/States';
import { orFailed, didFail, useReload } from '../lib/loadState';
import RatingColorBar from '../primitives/RatingColorBar';
import useTablePage from '../../../hooks/useTablePage';
import { fuelArbitrageInsight, topFuelMarketInsight, fuelWaypoints } from '../lib/insights';
import { fmtUsd, fmtUsdRange } from '../../../utils/format';
import { formatCorridorLabel, formatFuelWaypointLabel, formatStationAddress } from '../lib/labelFormat';
import WaypointFuelCard from '../primitives/WaypointFuelCard';
import { RI_TABLE_FRAME, RI_THEAD, RI_TH, RI_ROW, RI_TD } from '../lib/tableStyles';

// 25 rows per page, matching the other RI tables (corridors, origins-by-state,
// booked guests). Fuel-by-market returns one row per origin state — 48 for
// Verde Ranch — which rendered as one uninterrupted list.
const MARKET_PAGE_SIZE = 25;

// Uniform 1-decimal float formatter (matches the other RI tables).
const fmt1 = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(1));
// Miles values get thousands separators to match the comma grouping used in
// the corridor / map-origins tables (markup #84). Keeps one decimal: "2,244.9".
const fmtMiles = (v) => (v == null || Number.isNaN(Number(v))
  ? '—'
  : Number(v).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }));

// Per markup-comment #42, the hard-coded $50/$200 thresholds put every state
// in the "red" band on real data. Compute quartile cutoffs from the actual
// rendered rows so coloring is meaningful regardless of overall price level.
function buildFuelCostBands(rows) {
  if (!rows || !rows.length) return null;
  const prices = rows
    .map((r) => (r.avg_roundtrip_fuel_cost == null ? null : Number(r.avg_roundtrip_fuel_cost)))
    .filter((v) => v != null && Number.isFinite(v))
    .sort((a, b) => a - b);
  if (prices.length < 4) return null; // need enough points for meaningful quartiles
  const q = (p) => prices[Math.min(prices.length - 1, Math.floor((prices.length - 1) * p))];
  return { q1: q(0.25), q3: q(0.75) };
}

function fuelCostSeverityClass(cost, bands) {
  if (cost == null || !bands) return '';
  if (cost <= bands.q1) return 'text-emerald-600 dark:text-emerald-400';
  // Amber (not red) for the priciest markets: this column flags fuel
  // sensitivity, not an error/negative value — red on a dollar figure read as
  // "something is wrong" (markup #88).
  if (cost >= bands.q3) return 'text-amber-600 dark:text-amber-400';
  return '';
}

/**
 * A price date for display: "Oct 11, 2026". Reads the calendar date at the front of an
 * ISO date or timestamp (the backend's dates are UTC days) and formats it in UTC so the
 * reader's timezone cannot move it a day. Anything that does not start with a date is
 * returned as sent rather than hidden.
 * @param {string|null|undefined} value `prices_updated_at` or `sampled_week`.
 * @returns {string|null} The formatted date, or null when there is no value.
 */
export const formatPriceDate = (value) => {
  if (value == null || value === '') return null;
  const raw = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!m) return raw;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

/**
 * The trend's empty-state copy. A short range can fall between price readings; name the
 * date of the latest ones when we know it, so the reader can widen the range rather than
 * conclude there is no data.
 * @param {string|null|undefined} latest `prices_updated_at`, else the latest `sampled_week`.
 * @returns {string}
 */
export const trendEmptyMessage = (latest) => (latest
  ? `No price samples in this date range. The latest prices are from ${formatPriceDate(latest)} — widen the range to include them.`
  : 'No trend data yet.');

/**
 * The unit of the trend's price axis, from the first row that carries one: "USD / gallon"
 * or "CAD / litre". Null when no row names a currency and a unit, so no label is drawn.
 * @param {Array<object>|null|undefined} rows Trend rows.
 * @returns {string|null}
 */
export const trendAxisUnit = (rows) => {
  const row = (rows || []).find((r) => r?.currency && r?.unit);
  return row ? `${row.currency} / ${row.unit}` : null;
};

/** Card title for one waypoint: the park keeps its fixed label, corridors use the backend's city label. */
const waypointTitle = (w) => (w.waypoint_type === 'property'
  ? formatCorridorLabel(w.waypoint_label)
  : formatFuelWaypointLabel(w.display_label ?? w.waypoint_label));

export default function FuelIntelligenceTab({ clientId, range }) {
  const [summary, setSummary] = useState(null);
  const [cheapest, setCheapest] = useState(null);
  const [trend, setTrend] = useState(null);
  const [byMarket, setByMarket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState({});
  const [reloadKey, retry] = useReload();
  // The Gas Price Trend and By-Market card follow the global header date range
  // (`range`, from the page). Summary / Cheapest are current-price reads and stay
  // range-free. Fall back to the current calendar month if the header's range is
  // ever empty so the trend chart never queries with null bounds.
  const rangeStart = range?.from ?? null;
  const rangeEnd = range?.to ?? null;
  const thisMonth = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const eomDay = new Date(y, now.getMonth() + 1, 0).getDate();
    return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(eomDay).padStart(2, '0')}` };
  }, []);
  const effectiveFrom = rangeStart || thisMonth.from;
  const effectiveTo = rangeEnd || thisMonth.to;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      orFailed(getRiFuelSummary(clientId)),
      orFailed(getRiFuelCheapest(clientId)),
      orFailed(getRiFuelTrend(clientId, { from: effectiveFrom, to: effectiveTo })),
      orFailed(getRiFuelByMarket(clientId, { from: rangeStart, to: rangeEnd })),
    ]).then(([s, c, t, m]) => {
      if (cancelled) return;
      setFailed({ summary: didFail(s), cheapest: didFail(c), trend: didFail(t), byMarket: didFail(m) });
      setSummary(didFail(s) ? null : s?.data || null);
      setCheapest(didFail(c) ? [] : c?.data || []);
      setTrend(didFail(t) ? [] : t?.data || []);
      setByMarket(didFail(m) ? null : m?.data || null);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [clientId, effectiveFrom, effectiveTo, rangeStart, rangeEnd, reloadKey]);

  // Daily rows carry `point_date`; weekly rows only `sampled_week`. The axis reads whichever
  // the row has, in the order the backend sent them.
  const trendRows = useMemo(
    () => (trend || []).map((r) => ({ ...r, x: r.point_date ?? r.sampled_week })),
    [trend],
  );
  const axisUnit = useMemo(() => trendAxisUnit(trend), [trend]);
  const waypoints = useMemo(() => fuelWaypoints(summary), [summary]);
  const arbitrage = useMemo(() => fuelArbitrageInsight(waypoints), [waypoints]);
  const topMarket = useMemo(() => topFuelMarketInsight(byMarket?.rows), [byMarket]);

  // Declared before the `loading` early-return so the hook order stays stable
  // across renders. Resets to page 1 whenever a new fetch replaces the rows.
  const marketRows = byMarket?.rows;
  const {
    page: marketPage, setPage: setMarketPage, totalPages: marketTotalPages,
    sliceStart: marketSliceStart, sliceEnd: marketSliceEnd,
  } = useTablePage(marketRows?.length || 0, MARKET_PAGE_SIZE, [marketRows]);

  if (loading) return <RiLoading />;
  // Per markup-comment #40: don't flip Shell red vs Maverik white over a 1¢
  // difference. Compute median + spread once; color only when the spread is
  // meaningful and the station sits clearly above/below the median.
  const priceSpread = (() => {
    if (!cheapest?.length) return null;
    const prices = cheapest
      .map((c) => Number(c.fuel_price_regular_usd))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);
    if (prices.length === 0) return null;
    const min = prices[0];
    const max = prices[prices.length - 1];
    const mid = prices.length % 2
      ? prices[(prices.length - 1) / 2]
      : (prices[prices.length / 2 - 1] + prices[prices.length / 2]) / 2;
    return { min, max, median: mid, spread: max - min };
  })();
  const PRICE_MIN_SPREAD = 0.10;   // need ≥10¢ between cheapest/priciest
  const PRICE_DELTA_FROM_MEDIAN = 0.05; // ≥5¢ off median to flip color

  return (
    <div className="space-y-4">
      {/* 7A */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-center justify-between mb-1 max-[344px]:flex-col max-[344px]:items-stretch max-[344px]:gap-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Gas Price Summary by Waypoint</h3>
          <div className="flex flex-wrap items-center gap-2">
            {summary?.is_stale === true && (
              <span
                data-testid="fuel-stale-chip"
                className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:text-amber-300"
              >
                Prices may be out of date
              </span>
            )}
            {summary?.prices_updated_at ? (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Current prices · Updated {formatPriceDate(summary.prices_updated_at)}
              </span>
            ) : summary?.sampled_week && (
              <span className="text-xs text-gray-500 dark:text-gray-400">Week of {summary.sampled_week}</span>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Current prices · Average regular gas price at each waypoint guests pass through on the way to your property.</p>
        {failed.summary ? <RiError onRetry={retry} />
          : waypoints.length === 0 ? <RiEmpty message="No waypoint fuel data yet." />
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {waypoints.map((w) => (
                <WaypointFuelCard
                  key={w.display_label ?? w.waypoint_label}
                  label={waypointTitle(w)}
                  isProperty={w.waypoint_type === 'property'}
                  avgRegular={w.avg_regular}
                  cheapest={w.cheapest_regular}
                  mostExpensive={w.most_expensive_regular}
                  stations={w.stations_with_price}
                  avgDiesel={w.avg_diesel || null}
                />
              ))}
            </div>
          )
        }
        {arbitrage && arbitrage.diff !== 0 && (
          <div className="mt-3">
            <InsightCard title="Arbitrage">
              Property gas averages <strong>{fmtUsd(arbitrage.propertyAvg, { cents: true })}</strong>, corridor <strong>{fmtUsd(arbitrage.corridorAvg, { cents: true })}</strong> —
              {' '}guests save <strong>{fmtUsd(Math.abs(arbitrage.diff), { cents: true })}</strong>/gal filling up at the <strong>{arbitrage.cheaperAt}</strong>.
            </InsightCard>
          </div>
        )}
      </div>

      {/* 7B */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 overflow-x-auto responsive-card-table-wrapper">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Cheapest Regular Gas Near Your Property</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Current prices · Sorted by regular price — diesel varies independently.</p>
        {failed.cheapest ? <RiError onRetry={retry} />
          : (!cheapest || cheapest.length === 0) ? <RiEmpty message="No station price data yet." />
          : (
            <div className={RI_TABLE_FRAME}>
              <table className="w-full text-sm min-w-[640px] responsive-card-table">
              <thead className={RI_THEAD}>
                <tr>
                  <th className={`${RI_TH} text-left`}>Name</th>
                  <th className={`${RI_TH} text-left`}>Address</th>
                  <th className={`${RI_TH} text-right`}>Regular</th>
                  <th className={`${RI_TH} text-right`}>Diesel</th>
                  <th className={`${RI_TH} text-right`}>Miles</th>
                  <th className={`${RI_TH} text-left`}>Rating</th>
                  <th className={`${RI_TH} text-left`}>Map</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-gray-300">
                {cheapest.map((r, i) => {
                  const price = Number(r.fuel_price_regular_usd);
                  let priceClass = '';
                  if (priceSpread && priceSpread.spread >= PRICE_MIN_SPREAD && Number.isFinite(price)) {
                    if (price <= priceSpread.median - PRICE_DELTA_FROM_MEDIAN) {
                      priceClass = 'text-emerald-600 dark:text-emerald-400 font-medium';
                    } else if (price >= priceSpread.median + PRICE_DELTA_FROM_MEDIAN) {
                      priceClass = 'text-red-600 dark:text-red-400';
                    }
                  }
                  return (
                    <tr key={i} className={RI_ROW}>
                      <td className={`${RI_TD} font-medium text-gray-900 dark:text-white`} data-label="Name">{r.name || '—'}</td>
                      <td className={`${RI_TD} text-gray-600 dark:text-gray-300`} data-label="Address">{r.address ? formatStationAddress(r.address) : '—'}</td>
                      <td className={`${RI_TD} text-right tabular-nums ${priceClass}`} data-label="Regular">
                        {r.fuel_price_is_estimated && <span title="Estimated from same-week area average" className="text-gray-400 dark:text-gray-500 mr-0.5">~</span>}
                        {fmtUsd(Number.isFinite(price) ? price : 0, { cents: true })}
                      </td>
                      <td className={`${RI_TD} text-right tabular-nums`} data-label="Diesel">{fmtUsd(r.fuel_price_diesel_usd ? Number(r.fuel_price_diesel_usd) : 0, { cents: true })}</td>
                      <td className={`${RI_TD} text-right tabular-nums`} data-label="Miles">{r.miles_from_park != null ? `${fmtMiles(r.miles_from_park)} mi` : '—'}</td>
                      <td className={`${RI_TD} min-w-[110px]`} data-label="Rating"><RatingColorBar value={r.rating} /></td>
                      <td className={`${RI_TD}`} data-label="Map">
                        {r.google_maps_uri
                          ? <a href={r.google_maps_uri} target="_blank" rel="noreferrer" title="Open in Google Maps" className="text-base">🗺️</a>
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          )
        }
      </div>

      {/* 7C */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Gas Price Trend</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {effectiveFrom} → {effectiveTo}
          </span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Average gas price at stations around your property — daily readings where available, weekly samples before that.</p>
        {failed.trend ? <RiError onRetry={retry} />
          : (!trend || trend.length === 0) ? <RiEmpty message={trendEmptyMessage(summary?.prices_updated_at ?? summary?.sampled_week)} />
          : trend.length < 4 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 italic">
              Fewer than 4 price points in this date range — widen the range to plot a trend.
            </div>
          ) : (
            <div className="rotate-x-labels" style={{ height: 280 }} data-testid="fuel-trend-chart">
              <ResponsiveContainer>
                <ComposedChart data={trendRows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.15)" />
                  <XAxis dataKey="x" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    domain={['auto', 'auto']}
                    width={axisUnit ? 72 : 60}
                    label={axisUnit
                      ? { value: axisUnit, angle: -90, position: 'insideLeft', offset: 4, style: { textAnchor: 'middle', fontSize: 11, fill: '#9CA3AF' } }
                      : undefined}
                  />
                  <ThemedChartTooltip
                    formatter={(v) => fmtUsd(v, { cents: true })}
                    itemSorter={(item) => {
                      const order = { Regular: 0, Min: 1, Max: 2, Diesel: 3 };
                      return order[item?.name] ?? 99;
                    }}
                  />
                  {/* Custom legend: Recharts auto-derives in series order, so we
                      render our own list to keep Regular → Min → Max → Diesel. */}
                  <Legend
                    verticalAlign="bottom"
                    content={() => (
                      <div className="flex flex-wrap justify-center gap-4 text-xs mt-2">
                        {[
                          { label: 'Regular', color: '#32B1FF', kind: 'line' },
                          { label: 'Min', color: '#32B1FF', kind: 'rect' },
                          { label: 'Max', color: '#32B1FF', kind: 'rect' },
                          { label: 'Diesel', color: '#F97316', kind: 'line', dashed: true },
                        ].map((it) => (
                          <span key={it.label} className="inline-flex items-center gap-1">
                            {it.kind === 'line' ? (
                              <svg width="18" height="6" aria-hidden="true">
                                <line x1="0" y1="3" x2="18" y2="3" stroke={it.color} strokeWidth="2"
                                  strokeDasharray={it.dashed ? '4 3' : undefined} />
                              </svg>
                            ) : (
                              <span className="inline-block w-3 h-3 rounded-sm"
                                style={{ backgroundColor: it.color, opacity: 0.35 }} />
                            )}
                            <span style={{ color: it.color }}>{it.label}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  />
                  <Area type="monotone" dataKey="max_regular" name="Max" stroke="none" fill="#32B1FF" fillOpacity={0.15} />
                  <Area type="monotone" dataKey="min_regular" name="Min" stroke="none" fill="#fff" fillOpacity={0.0} />
                  <Line type="monotone" dataKey="avg_regular" name="Regular" stroke="#32B1FF" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="avg_diesel" name="Diesel" stroke="#F97316" strokeWidth={2} strokeDasharray="4 3" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )
        }
      </div>

      {/* 7D */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 overflow-x-auto responsive-card-table-wrapper">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Fuel Cost Impact by Market</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Estimated fuel spend per guest by origin market, sorted by highest per-guest spend. Markets at the top are the most fuel-sensitive — gas-price swings hit those guests' trip economics hardest.</p>
        {failed.byMarket ? <RiError onRetry={retry} />
          : (!byMarket?.rows || byMarket.rows.length === 0) ? <RiEmpty message="No market fuel data yet." />
          : (() => {
            const fuelCostBands = buildFuelCostBands(byMarket.rows);
            return (
            <>
            {fuelCostBands && (
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-2" aria-label="Avg cost color key">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  Lower fuel cost
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500" />
                  Higher — most fuel-sensitive
                </span>
              </div>
            )}
            <div className={RI_TABLE_FRAME}>
              <table className="w-full text-sm min-w-[560px] responsive-card-table">
              <thead className={RI_THEAD}>
                <tr>
                  <th className={`${RI_TH} text-left`}>State</th>
                  <th className={`${RI_TH} text-right`}>Origins</th>
                  <th className={`${RI_TH} text-right`} title="Average gallons burned on a one-way trip to the property">Avg gal (one-way)</th>
                  <th className={`${RI_TH} text-right`} title="Average fuel spend per guest for the round trip (there and back)">Avg cost (round-trip)</th>
                  <th className={`${RI_TH} text-right`} title="Min–max round-trip fuel spend across origins in this market">Range (round-trip)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-gray-300">
                {byMarket.rows.slice(marketSliceStart, marketSliceEnd).map((r) => {
                  const cost = r.avg_roundtrip_fuel_cost != null ? Number(r.avg_roundtrip_fuel_cost) : null;
                  return (
                    <tr key={r.state} className={RI_ROW}>
                      <td className={`${RI_TD} font-medium text-gray-900 dark:text-white`} data-label="State">{r.state}</td>
                      <td className={`${RI_TD} text-right tabular-nums`} data-label="Origins">{r.origins != null ? Number(r.origins).toLocaleString() : '—'}</td>
                      <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg gal (one-way)">{fmt1(r.avg_fuel_gallons)}</td>
                      <td className={`${RI_TD} text-right tabular-nums font-medium ${fuelCostSeverityClass(cost, fuelCostBands)}`} data-label="Avg cost (round-trip)">{fmtUsd(cost ?? 0, { cents: true })}</td>
                      <td className={`${RI_TD} text-right tabular-nums text-gray-500 dark:text-gray-400`} data-label="Range (round-trip)">{fmtUsdRange(r.min_fuel_cost ?? 0, r.max_fuel_cost ?? 0, { cents: true })}</td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
            {/* Colour bands above are computed from ALL rows, not the current
                page, so a state keeps the same severity colour as you page. */}
            <RiPagination
              page={marketPage}
              totalPages={marketTotalPages}
              onPageChange={setMarketPage}
              totalItems={byMarket.rows.length}
              itemLabel="market"
            />
            </>
            );
          })()
        }
        {byMarket?.rows && byMarket.rows.length > 0 && <RiSparseNotice count={byMarket.rows.length} />}
        {topMarket && (
          <div className="mt-3">
            <InsightCard title="Top Cost Market">
              A guest from <strong>{topMarket.state}</strong> spends an average of <strong>{fmtUsd(topMarket.cost)}</strong> on round-trip fuel (at {fmtUsd(byMarket?.avg_gas, { cents: true })}/gal{byMarket?.avg_gas_is_estimated ? <span title="No station prices found for this property — using the $3.50 default" className="text-gray-400 dark:text-gray-500"> · default</span> : null}).
            </InsightCard>
          </div>
        )}
      </div>
    </div>
  );
}
